// pages/master/role/CompanyManage.jsx
'use client';

import { useState, useRef, useMemo } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  TextField,
  InputAdornment,
  TablePagination
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import BusinessIcon from '@mui/icons-material/Business';
import SearchIcon from '@mui/icons-material/Search';

import FormDialog from '../../../../components/FormDialog';
import { useNotifier } from '../../../../components/Notifier';
import { useCompanyList } from '../../../../stores/companyStore';
import { useLanguage } from '../../../../utils/i18n';

import CompanyForRoleDialog from './CompanyForRoleDialog';

const CompanyManage = ({ roleName, dataCompanyList, onRefresh }) => {
  const notify = useNotifier();
  const { t } = useLanguage();
  const { companyOptions } = useCompanyList();

  const companyIds = useMemo(() => {
    if (!dataCompanyList) return [];
    if (Array.isArray(dataCompanyList) && dataCompanyList.length > 0) {
      return dataCompanyList[0].company_id || [];
    }
    return dataCompanyList.company_id || [];
  }, [dataCompanyList]);

  const assignedCompanies = useMemo(() => {
    if (!companyIds || companyIds.length === 0) return [];
    if (!companyOptions || companyOptions.length === 0) {
      return companyIds.map((id) => ({ id, name: `Company ID: ${id}` }));
    }

    return companyIds.map((id) => {
      const match = companyOptions.find((opt) => String(opt.value) === String(id));
      return {
        id,
        name: match ? match.label : `Company ID: ${id}`
      };
    });
  }, [companyIds, companyOptions]);

  // --- STATE PENCARIAN & PAGINATION ---
  const [searchKeyword, setSearchKeyword] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // 1. Filter Data berdasarkan Pencarian
  const filteredCompanies = useMemo(() => {
    if (!searchKeyword) return assignedCompanies;
    const lowerKeyword = searchKeyword.toLowerCase();

    return assignedCompanies.filter((c) => (c.name && c.name.toLowerCase().includes(lowerKeyword)) || String(c.id).includes(lowerKeyword));
  }, [assignedCompanies, searchKeyword]);

  // 2. Potong Data berdasarkan Pagination (Client-Side)
  const paginatedCompanies = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredCompanies.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredCompanies, page, rowsPerPage]);

  // --- HANDLER PENCARIAN & PAGINATION ---
  const handleSearchChange = (e) => {
    setSearchKeyword(e.target.value);
    setPage(0); // Reset ke halaman pertama setiap kali mencari
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // --- MODAL & SAVE LOGIC ---
  const companyDialogRef = useRef(null);
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [companyDialogDirty, setCompanyDialogDirty] = useState(false);
  const [companySaving, setCompanySaving] = useState(false);

  const handleEditClick = () => setCompanyDialogOpen(true);
  const handleCompanyDialogClose = () => setCompanyDialogOpen(false);

  const handleDialogCompanyAccessSave = async () => {
    if (!companyDialogRef.current || typeof companyDialogRef.current.save !== 'function') return;

    setCompanySaving(true);
    try {
      await companyDialogRef.current.save();
      notify({ message: t('notify.saved', 'Saved successfully'), severity: 'success' });
      setCompanyDialogOpen(false);
      if (typeof onRefresh === 'function') {
        onRefresh();
      }
    } catch (err) {
      console.error('Save company access error', err);
      notify({ message: err?.message ?? t('notify.failedSave', 'Failed to save data'), severity: 'error' });
    } finally {
      setCompanySaving(false);
      setCompanyDialogDirty(false);
    }
  };

  return (
    <Box>
      {/* HEADER & ACTIONS */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h6" color="primary" fontWeight="bold">
          {t('masterRole.assignedCompanies', 'Assigned Companies')}
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* Input Pencarian */}
          <TextField
            size="small"
            placeholder={t('masterRole.selectCompanyPlaceholder', 'Search or select company')}
            value={searchKeyword}
            onChange={handleSearchChange}
            sx={{ width: 300, bgcolor: '#fff' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
          />

          <Button
            variant="contained"
            color="info"
            startIcon={<EditIcon />}
            onClick={handleEditClick}
            disableElevation
            sx={{ textTransform: 'none', height: 40 }}
          >
            {t('masterRole.editCompanyAccess', 'Edit Company Access')}
          </Button>
        </Box>
      </Box>

      {/* CONTAINER LIST DENGAN SCROLL & PAGINATION */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {/* Bagian List yang bisa di-scroll (maksimal ~60% tinggi layar) */}
        <Box sx={{ maxHeight: 'calc(110vh - 350px)', overflowY: 'auto' }}>
          {paginatedCompanies.length > 0 ? (
            <List sx={{ p: 0 }}>
              {paginatedCompanies.map((company, index) => (
                <Box key={company.id}>
                  <ListItem sx={{ py: 0.5, gap: 2 }}>
                    <ListItemIcon>
                      <BusinessIcon color="info" />
                    </ListItemIcon>
                    <ListItemText
                      primary={company.name}
                      // secondary={`Company ID: ${company.id}`} // Opsional, ditambahkan agar ID terlihat
                      primaryTypographyProps={{ fontWeight: 500 }}
                    />
                  </ListItem>
                  {index < paginatedCompanies.length - 1 && <Divider />}
                </Box>
              ))}
            </List>
          ) : (
            <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              <BusinessIcon sx={{ fontSize: 48, color: '#e0e0e0', mb: 1 }} />
              <Typography>
                {searchKeyword
                  ? t('general.noDataMatch', 'No data matches the search.')
                  : t('masterRole.noCompanySelected', 'No companies selected yet.')}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Pagination Component */}
        <Divider />
        <TablePagination
          component="div"
          count={filteredCompanies.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage={t('general.rowsPerPage', 'Rows per page:')}
        />
      </Paper>

      {/* MODAL EDIT COMPANY ACCESS */}
      <FormDialog
        open={companyDialogOpen}
        onClose={handleCompanyDialogClose}
        title={`${t('masterRole.manageCompany', 'Manage Company')} | Role: ${roleName || '-'}`}
        fullScreenMode={true}
        fullHeight={true}
        actions={
          <>
            <Button
              variant="contained"
              disabled={!companyDialogDirty || companySaving}
              onClick={handleDialogCompanyAccessSave}
              sx={{ backgroundColor: '#0072DA' }}
            >
              {companySaving ? t('general.processing', 'Processing...') : t('general.submit', 'Submit')}
            </Button>
            <Button onClick={handleCompanyDialogClose} disableRipple disableElevation variant="outlined">
              {t('general.cancel', 'Cancel')}
            </Button>
          </>
        }
      >
        <CompanyForRoleDialog
          ref={companyDialogRef}
          roleName={roleName}
          dataCompanyList={dataCompanyList}
          onDirtyChange={setCompanyDialogDirty}
        />
      </FormDialog>
    </Box>
  );
};

export default CompanyManage;
